import { merge, isEmpty } from 'lodash';
import heydoctor from 'app/vendor/heydoctor';
import * as Analytics from 'app/lib/analytics';
import { cookies } from 'app/lib/storage';
import { Stores, Store } from 'app/lib/synaptik';
import { Visit, Coupon, Visit as VisitType } from 'app/types';

interface State {
  visit: Visit | null;
  questionnaire: Record<any, any>;
  photoUploadsInProgress: Record<any, any>;
  visits: Visit[];
  coupons: Record<string, Coupon>;

  fetchingHistory?: boolean;
  fetching?: boolean;
  creating?: boolean;
  submittingQuestionnaire?: boolean;
  uploadingPhoto?: boolean;
  applyingPromotion?: boolean;
  updatingPharmacy?: boolean;
  fetchingCoupons?: boolean;
  submittingPayment?: boolean;
  removingPromotion?: boolean;
  cancellingVisit?: boolean;
  removingPromo?: boolean;
  applyingPromo?: boolean;
  postingConsent?: boolean;
}

export class VisitsStore extends Store<State, Stores> {
  state: State = {
    /**
     * Visit currently in progress
     */
    visit: null,
    questionnaire: {},
    photoUploadsInProgress: {},
    visits: [],
    coupons: {},
  };

  fetchHistory = () =>
    this.runAsync({
      key: 'fetchingHistory',
      work: async () => {
        const visits = await heydoctor.visits.fetchHistory();

        // @ts-ignore
        this.setState({ visits });
      },
    });

  fetch = (id: any) =>
    this.runAsync({
      key: 'fetching',
      work: async () => {
        const visit = await heydoctor.visits.fetch(id);

        await this.setActiveVisit(visit);

        return visit as VisitType;
      },
    });

  create = ({
    code,
    state,
    medicalHistoryTaskEnabled,
  }: {
    code: string;
    state: string;
    medicalHistoryTaskEnabled?: boolean;
  }) =>
    this.runAsync({
      key: 'creating',
      work: async () => {
        const visit = await heydoctor.visits.create({
          code,
          pharmacyId: cookies.get()?.queryParams?.pharmacyId as string,
          promoCode: cookies.get()?.queryParams?.promoCode as string,
          state,
          medicalHistoryTaskEnabled,
        });

        this.stores.questionnaire.reset();

        await this.setActiveVisit(visit);

        Analytics.onVisitStarted();

        return visit;
      },
    });

  setActiveVisit = async (visit: any) => {
    this.setState({
      visit,
    });
  };

  isVisitClosed = () =>
    !!this.state.visit &&
    (this.state.visit?.isVisitComplete() ||
      this.state.visit?.isVisitCancelled() ||
      this.state.visit?.isVisitAbandoned());

  async updateLocalVisit(updates: any) {
    await this.setActiveVisit(merge({}, this.state.visit, updates));
  }

  async updateLocalTask(code: any, updates: any) {
    const { visit } = this.state;

    await this.updateLocalVisit({
      tasks: {
        [code]: merge({}, visit?.tasks[code], updates),
      },
    });
  }

  completeTask = async (taskCode: any) => {
    const visit = this.state.visit!.completeTask(taskCode);
    // @ts-ignore
    this.setState({ visit });
    // @ts-ignore
    Analytics.onTaskCompleted({ service_code: this.state.visit?.service.code, task_code: taskCode });

    return visit;
  };

  postConsent = async () => {
    this.runAsync({
      key: 'postingConsent',
      work: async () => {
        const visit = await this.state.visit!.postConsent();
        // @ts-ignore
        this.setState({ visit });
      },
    });
  };

  getBaseVisitCost = () => {
    const { service } = this.state.visit!;

    return service.retailCost / 100 + service.fixedCost / 100;
  };

  getActualVisitCost = () => {
    const { promotion } = this.state.visit!;
    let cost = this.getBaseVisitCost();

    if (promotion) {
      cost = this.state.visit?.getDiscountedCost(cost, promotion.discountType, promotion.discountAmount) as number;
    }
    return cost;
  };

  submitQuestionnaire = (answers: any) =>
    this.runAsync({
      key: 'submittingQuestionnaire',
      work: async () => {
        if (isEmpty(answers) || !this.stores.questionnaire.isQuestionnaireComplete()) {
          throw Error('Intake interview empty/invalid');
        }

        const visit = await this.state.visit?.postAnswers(answers);
        this.setActiveVisit(visit);
        this.stores.questionnaire.reset();
      },
    });
  /** End Questionnaire */

  uploadPhoto = async (
    photoId: number,
    photoData: File | Blob | string,
    onUploadProgress?: (percent: number) => void
  ) =>
    this.runAsync({
      key: `uploadingPhoto`,
      work: async () => {
        const visit = (await this.state.visit?.postPhotoFormData(photoId, photoData, onUploadProgress)) as Visit;

        const updatedPhoto = visit.photos.find((p) => p.id === photoId);

        if (updatedPhoto && this.state.visit) {
          //update local instance of visit with updated photo information
          const updatedVisit = this.state.visit?.handlePostPhotoUpload(photoId, updatedPhoto.fileId);

          return this.setActiveVisit(updatedVisit);
        }

        await this.setActiveVisit(visit);
      },
    });

  updatePharmacy = async ({ type, pharmacy, deliveryAddress }: any) =>
    this.runAsync({
      key: 'updatingPharmacy',
      work: async () => {
        const visit = await this.state.visit?.postPharmacy(type, pharmacy, deliveryAddress);
        this.setActiveVisit(visit);
      },
    });

  applyPromotion = (code: any) =>
    this.runAsync({
      key: 'applyingPromotion',
      work: async () => {
        const visit = await this.state.visit?.applyPromotion(code);

        Analytics.onPromoCodeApplied();

        this.setActiveVisit(visit);
      },
    });

  removePromotion = () =>
    this.runAsync({
      key: 'removingPromotion',
      work: async () => {
        const visit = await this.state.visit?.removePromotion();
        this.setActiveVisit(visit);
      },
    });

  submitPayment = async (token: string) =>
    this.runAsync({
      key: 'submittingPayment',
      work: async () => {
        const visit = await this.state.visit?.postPayment(token);
        this.setActiveVisit(visit);

        Analytics.onPaymentReceived({
          amount: this.getActualVisitCost(),
          // @ts-ignore
          code: visit?.service.code,
        });
      },
    });

  cancelVisit = (visit: { cancelVisit?: any; id?: any; status?: any }) =>
    this.runAsync({
      key: 'cancellingVisit',
      work: async () => {
        await visit.cancelVisit();

        const { id, status } = visit;
        Analytics.trackEvent('Visit Cancelled', { id, status });

        this.setState({
          visits: this.state.visits.filter((visitItem: { id: any }) => visitItem.id !== id),
        });
      },
    });

  fetchCoupons = async (visitId: any) =>
    this.runAsync({
      key: 'fetchingCoupons',
      work: async () => {
        const { visit } = this.state;
        if (!visit?.signedAt) return [];

        // @ts-ignore
        const coupons = await heydoctor.visits.fetchCoupons(visitId);

        this.setState({
          coupons: {
            [visitId]: coupons,
          },
        });

        return coupons;
      },
    });
}
